import Alert from 'Components/Eftpos/Alert';
import { HashProvider } from 'Hooks/UseHash';
import { ReactNode, useEffect, useState } from 'react';
import BankAccountForm, { EditableClientBankAccount } from './BankAccountForm';
import UseUserApi, { ErrorHandler } from 'Services/UserApi';
import Modal from 'Abstracts/Components/Modal';
import ModalFrame from 'Abstracts/Components/ModalFrame';
import useMobileVh from 'Hooks/UseMobileVh';

export default function BankAccountPrompt(props: { hash: string }) {
	const { hash } = props;

	useMobileVh();

	return (
		<HashProvider hash={hash}>
			<div className="container" style={{ paddingTop: '15px' }}>
				<Content />
			</div>
		</HashProvider>
	);
}

interface ContentState {
	mode: 'add' | 'adding' | 'added' | 'error';
	open: boolean;
	bankAccount: EditableClientBankAccount;
}

function Content() {
	const [state, setState] = useState<ContentState>({
		mode: 'add',
		open: false,
		bankAccount: {
			ClientBankAccountName: '',
			ClientBankAccountBsb: '',
			ClientBankAccountAccount: '',
		},
	});

	const userApi = UseUserApi();
	useEffect(() => {
		userApi<{ requiresPrompt: boolean }>({
			route: 'bank-account/api',
			data: {
				action: 'requiresPrompt',
			},
			errorMessage: 'Error checking bank account',
		})
			.then((res) => {
				if (res.data.requiresPrompt) {
					setState((c) => ({ ...c, open: true }));
				}
			})
			.catch((e: unknown) => {
				console.error(e);
			});
	}, [userApi]);

	const addBankAccount = (finalBankAccount: EditableClientBankAccount) => {
		setState((c) => ({ ...c, mode: 'adding' }));
		return userApi({
			route: 'bank-account/api',
			data: {
				action: 'create',
				bankAccount: finalBankAccount,
			},
			errorMessage: 'Error saving bank account details',
		})
			.then(() => setState((c) => ({ ...c, mode: 'added' })))
			.catch((e: unknown) => {
				ErrorHandler.toast(e);
				setState((c) => ({ ...c, mode: 'error' }));
			});
	};

	let content: ReactNode = (
		<Alert className="alert alert-warning alert-small alert-column" style={{ marginBottom: '12px' }}>
			<i className="fa fa-exclamation-triangle fa-lg" />
			<span>
				Thank you for your booking at our market and event. You have a pending refund. In order to process your
				refund, please add your bank account details as soon as possible. We thank you for your patience with
				the refund processing. If you have any questions, please reach out to your market manager for
				assistance.
			</span>
		</Alert>
	);
	if (state.mode === 'added') {
		content = (
			<Alert className="alert alert-success alert-small" style={{ marginBottom: '12px' }}>
				<i className="fa fa-check-circle fa-lg" />
				<span>Bank account details saved successfully.</span>
			</Alert>
		);
	}

	if (state.mode === 'error') {
		content = (
			<Alert className="alert alert-danger alert-small" style={{ marginBottom: '12px' }}>
				<i className="fa fa-exclamation-circle fa-lg" />
				<span>There was an error saving your bank account details.</span>
			</Alert>
		);
	}

	return (
		<Modal open={state.open} onClose={() => setState((c) => ({ ...c, open: false }))}>
			<ModalFrame size={'sm'} padding="2rem" title="Add bank account">
				{content}
				{state.mode !== 'added' ? (
					<BankAccountForm
						bankAccount={state.bankAccount}
						setBankAccount={(updated) => setState((c) => ({ ...c, bankAccount: { ...updated } }))}
						onSave={addBankAccount}
						saveButtonText="Save bank account details"
					/>
				) : null}
			</ModalFrame>
		</Modal>
	);
}
